/**
 * Gaming Product Form JavaScript
 * Handles variant selection and product form functionality
 */

class GamingProductForm {
  constructor(form) {
    this.form = form;
    this.variantIdInput = form.querySelector('#product-variant-id');
    this.variantData = JSON.parse(document.getElementById('product-variants-data')?.textContent || '[]');
    this.optionSelectors = {
      toggles: form.querySelectorAll('.gaming-variant-toggle__option'),
      selects: form.querySelectorAll('.gaming-variant-option__select'),
      hiddenInputs: form.querySelectorAll('.gaming-variant-hidden-input')
    };
    this.isSyncing = false;
    this.colorOptionPosition = form.dataset.colorOptionPosition ? parseInt(form.dataset.colorOptionPosition, 10) : 0;

    this.initEventListeners();
  }

  initEventListeners() {
    // Set initial state from URL if needed
    this.updateStateFromUrl();
  }

  /**
   * Update form state based on URL variant parameter
   */
  updateStateFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    
    if (variantId) {
      const variant = this.variantData.find(v => v.id == variantId);
      if (variant) {
        // Update form UI to match the variant's options
        this.updateFormToMatchVariant(variant);
      }
    }
  }

  /**
   * Update all form elements to match a specific variant
   */
  updateFormToMatchVariant(variant, options = {}) {
    if (!variant) return;

    if (this.isSyncing) {
      return;
    }

    this.isSyncing = true;

    try {
      // Update variant ID input
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
      }

      // Update UI elements for each option
      variant.options.forEach((optionValue, index) => {
        const position = index + 1;
        
        // Update toggles
        const toggle = this.form.querySelector(`.gaming-variant-toggle__option[data-option-position="${position}"][data-option-value="${optionValue}"]`);
        if (toggle) {
          this.selectToggleOption(toggle, { shouldUpdateVariant: false });
        }

        // Update selects
        const select = this.form.querySelector(`.gaming-variant-option__select[data-option-position="${position}"]`);
        if (select) {
          select.value = optionValue;
        }

        // Update hidden inputs
        const hiddenInput = this.form.querySelector(`.gaming-variant-hidden-input[data-option-position="${position}"]`);
        if (hiddenInput) {
          hiddenInput.value = optionValue;
        }
      });

      // Update price and availability
      this.updatePriceDisplay(variant);
      this.updateAddToCartButton(variant);
      
      // Update images based on variant
      this.updateImagesForVariant(variant);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Handle click on toggle option
   */
  selectToggleOption(toggle, { shouldUpdateVariant = true } = {}) {
    const position = toggle.dataset.optionPosition;
    const value = toggle.dataset.optionValue;
    const toggleGroup = toggle.closest('.gaming-variant-toggle');
    
    if (!toggleGroup) return;

    // Update toggle UI
    const options = toggleGroup.querySelectorAll('.gaming-variant-toggle__option');
    options.forEach(opt => {
      opt.classList.remove('active');
      opt.setAttribute('aria-pressed', 'false');
    });
    
    toggle.classList.add('active');
    toggle.setAttribute('aria-pressed', 'true');
    
    // Update indicator
    const indicator = toggleGroup.querySelector('.gaming-variant-toggle__indicator');
    if (indicator) {
      const buttonIndex = Array.from(options).indexOf(toggle);
      indicator.style.transform = `translateX(${buttonIndex * 100}%)`;
    }
    
    // Update hidden input
    const hiddenInput = this.form.querySelector(`.gaming-variant-hidden-input[data-option-position="${position}"]`);
    if (hiddenInput) {
      hiddenInput.value = value;
    }
    
    // Find matching variant and update form
    if (shouldUpdateVariant && !this.isSyncing) {
      this.updateVariantFromOptions();
    }
  }

  /**
   * Find matching variant based on selected options
   */
  updateVariantFromOptions() {
    if (this.isSyncing) {
      return;
    }

    // Get all selected options
    const selectedOptions = [];
    
    // Get from hidden inputs
    this.optionSelectors.hiddenInputs.forEach(input => {
      const position = parseInt(input.dataset.optionPosition, 10) - 1;
      if (!isNaN(position)) {
        selectedOptions[position] = input.value;
      }
    });
    
    // Get from selects
    this.optionSelectors.selects.forEach(select => {
      const position = parseInt(select.dataset.optionPosition, 10) - 1;
      if (!isNaN(position)) {
        selectedOptions[position] = select.value;
      }
    });
    
    // Find matching variant
    const matchingVariant = this.variantData.find(variant => {
      return variant.options.every((option, index) => 
        option === selectedOptions[index]
      );
    });
    
    if (matchingVariant) {
      // Update form
      this.updateFormToMatchVariant(matchingVariant);
      
      // Update URL
      if (history.replaceState) {
        const newUrl = window.location.pathname + '?variant=' + matchingVariant.id;
        history.replaceState(null, '', newUrl);
      }
    }
  }

  /**
   * Update price display with the variant's price
   */
  updatePriceDisplay(variant) {
    const priceElement = document.getElementById('price-main');
    if (priceElement && variant) {
      const priceDisplay = priceElement.querySelector('.gaming-product-hero__price-display');
      if (priceDisplay) {
        // Create price HTML
        let priceHtml = `<span class="price-item price-item--regular">${formatMoney(variant.price)}</span>`;
        
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceHtml = `
            <span class="price-item price-item--sale">${formatMoney(variant.price)}</span>
            <span class="price-item price-item--regular">${formatMoney(variant.compare_at_price)}</span>
          `;
        }
        
        priceDisplay.innerHTML = priceHtml;
      }
    }
  }

  /**
   * Update add to cart button state based on variant availability
   */
  updateAddToCartButton(variant) {
    const button = this.form.querySelector('.gaming-product-hero__add-to-cart');
    if (!button) return;
    
    const buttonText = button.querySelector('span');
    if (!buttonText) return;
    
    if (variant && variant.available) {
      button.disabled = false;
      buttonText.textContent = window.themeStrings?.addToCart || 'Add to cart';
    } else {
      button.disabled = true;
      buttonText.textContent = window.themeStrings?.soldOut || 'Sold out';
    }
  }

  /**
   * Update hero and side specification images based on variant
   */
  updateImagesForVariant(variant) {
    if (!variant || !this.colorOptionPosition) return;

    // Get the color value from the variant
    const colorValue = variant.options[this.colorOptionPosition - 1];
    if (!colorValue) return;

    const colorSlug = colorValue.toLowerCase().trim();

    // Update hero image and thumbnails
    this.updateHeroImage(colorSlug);
    
    // Update side specification image
    this.updateSideSpecImage(colorSlug);
  }

  /**
   * Update the main hero image and show/hide thumbnails based on color
   */
  updateHeroImage(colorSlug) {
    const thumbnails = document.querySelectorAll('.gaming-product-hero__thumbnail');
    const mainImage = document.querySelector('.gaming-product-hero__image');
    
    if (!thumbnails.length || !mainImage) return;

    let firstVisibleThumbnail = null;

    // Show/hide thumbnails based on color
    thumbnails.forEach(thumbnail => {
      const thumbnailColor = thumbnail.dataset.variantColor || '';
      
      // Show if no color tag or matches current color
      if (thumbnailColor === '' || thumbnailColor === colorSlug) {
        thumbnail.classList.remove('hidden');
        thumbnail.removeAttribute('aria-hidden');
        
        if (!firstVisibleThumbnail) {
          firstVisibleThumbnail = thumbnail;
        }
      } else {
        thumbnail.classList.add('hidden');
        thumbnail.setAttribute('aria-hidden', 'true');
        thumbnail.classList.remove('gaming-product-hero__thumbnail--active');
      }
    });

    // Update main image to first visible thumbnail
    if (firstVisibleThumbnail) {
      thumbnails.forEach(thumb => thumb.classList.remove('gaming-product-hero__thumbnail--active'));
      firstVisibleThumbnail.classList.add('gaming-product-hero__thumbnail--active');
      
      const newImageUrl = firstVisibleThumbnail.dataset.imageUrl;
      if (newImageUrl) {
        mainImage.style.opacity = '0.5';
        setTimeout(() => {
          mainImage.src = newImageUrl;
          mainImage.style.opacity = '1';
        }, 150);
      }
    }
  }

  /**
   * Update the side specification image based on color
   */
  updateSideSpecImage(colorSlug) {
    const sideImage = document.querySelector('.gaming-product-full-specs__side-image');
    if (!sideImage) return;

    // Find matching side view image from thumbnails
    const thumbnails = document.querySelectorAll('.gaming-product-hero__thumbnail');
    let matchingSideImage = null;

    // First try to find a side view image with matching color
    thumbnails.forEach(thumbnail => {
      const thumbnailColor = thumbnail.dataset.variantColor || '';
      const imageUrl = thumbnail.dataset.imageUrl;
      const alt = thumbnail.querySelector('img')?.alt || '';
      const altLower = alt.toLowerCase();
      
      // Check if this is a side view image
      if ((altLower.includes('side view') || altLower.includes('[view:side]')) && 
          (thumbnailColor === colorSlug || thumbnailColor === '')) {
        if (!matchingSideImage || thumbnailColor === colorSlug) {
          matchingSideImage = {
            url: imageUrl,
            alt: alt
          };
        }
      }
    });

    // Update the side image if we found a match
    if (matchingSideImage) {
      sideImage.style.opacity = '0.5';
      setTimeout(() => {
        sideImage.src = matchingSideImage.url.replace('width=800', 'width=600');
        sideImage.alt = matchingSideImage.alt;
        sideImage.dataset.activeColor = colorSlug;
        sideImage.style.opacity = '1';
      }, 150);
    }
  }
}

// Helper function for formatting money
function formatMoney(cents, format) {
  if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    return Shopify.formatMoney(cents, format);
  }
  
  if (typeof cents == 'string') { cents = cents.replace('.',''); }
  const value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = (format || theme?.moneyFormat || '${{amount}}');
  
  function defaultOption(opt, def) {
    return (typeof opt == 'undefined' ? def : opt);
  }
  
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');
    
    if (isNaN(number) || number == null) { return 0; }
    
    number = (number/100.0).toFixed(precision);
    
    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? (decimal + parts[1]) : '';
    
    return dollars + cents;
  }
  
  switch(formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }
  
  return formatString.replace(placeholderRegex, value);
}

// Initialize forms when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const productForms = document.querySelectorAll('form[data-type="add-to-cart-form"]');
  productForms.forEach(form => {
    // Store the instance on the form element for reference by other code
    form.gamingProductForm = new GamingProductForm(form);
  });
});
